/**
 * Role-Based Access Control Hook
 * Comprehensive RBAC system for the AFF application
 */

import { useCallback } from 'react';
import { useAuth } from './useAuth';

export const useRBAC = () => {
  const { user, isAuthenticated } = useAuth();

  // Get user role
  const getUserRole = useCallback(() => {
    if (!user) return null;
    return user.user_role || user.role || 'Regular User';
  }, [user]);

  // Check if user has specific role
  const hasRole = useCallback((requiredRole) => {
    if (!isAuthenticated || !user) return false;
    
    const userRole = getUserRole();
    
    // Role hierarchy: Superuser > Staff > Regular User
    const roleHierarchy = {
      'Superuser': 3,
      'Staff': 2,
      'Regular User': 1
    };
    
    const userLevel = roleHierarchy[userRole] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;
    
    return userLevel >= requiredLevel;
  }, [isAuthenticated, user, getUserRole]);

  // Check if user is admin (Superuser or Staff)
  const isAdmin = useCallback(() => {
    return hasRole('Staff');
  }, [hasRole]);

  // Check if user is superuser
  const isSuperuser = useCallback(() => {
    return hasRole('Superuser');
  }, [hasRole]);

  // Check if user is staff
  const isStaff = useCallback(() => {
    return getUserRole() === 'Staff';
  }, [getUserRole]);

  // Check if user is regular user
  const isRegularUser = useCallback(() => {
    return getUserRole() === 'Regular User';
  }, [getUserRole]);

  // Check if user can perform specific action
  const canPerformAction = useCallback((action) => {
    if (!isAuthenticated || !user) return false;

    const userRole = getUserRole();
    
    // Define permissions for each role
    const permissions = {
      'Superuser': [
        // User Management
        'users.view', 'users.create', 'users.edit', 'users.delete',
        'users.approve', 'users.bulk_action',
        
        // Member Management
        'members.view', 'members.create', 'members.edit', 'members.delete',
        'members.approve', 'members.bulk_action',
        
        // Financial Management
        'invoices.view', 'invoices.create', 'invoices.edit', 'invoices.delete',
        'invoices.bulk_action', 'invoices.export',
        
        // Integration Management
        'integrations.view', 'integrations.manage', 'integrations.sync',
        
        // Analytics & Reports
        'analytics.view', 'reports.generate', 'reports.export',
        
        // System Administration
        'system.settings', 'system.maintenance', 'system.logs'
      ],
      'Staff': [
        // User Management
        'users.view', 'users.create', 'users.edit', 'users.approve', 'users.bulk_action',
        
        // Member Management
        'members.view', 'members.create', 'members.edit', 'members.delete',
        'members.approve', 'members.bulk_action',
        
        // Financial Management
        'invoices.view', 'invoices.create', 'invoices.edit', 'invoices.bulk_action',
        
        // Integration Management
        'integrations.view', 'integrations.sync',
        
        // Analytics & Reports
        'analytics.view', 'reports.generate'
      ],
      'Regular User': [
        // Limited access
        'profile.view', 'profile.edit',
        'members.view_limited', 'invoices.view_own'
      ]
    };

    const userPermissions = permissions[userRole] || [];
    return userPermissions.includes(action);
  }, [isAuthenticated, user, getUserRole]);

  // Check if user can access specific resource
  const canAccessResource = useCallback((resource, action = 'view') => {
    const permission = `${resource}.${action}`;
    return canPerformAction(permission);
  }, [canPerformAction]);

  // Check if user can manage users
  const canManageUsers = useCallback(() => {
    return canPerformAction('users.view') && canPerformAction('users.edit');
  }, [canPerformAction]);

  // Check if user can manage members
  const canManageMembers = useCallback(() => {
    return canPerformAction('members.view') && canPerformAction('members.edit');
  }, [canPerformAction]);

  // Check if user can manage invoices
  const canManageInvoices = useCallback(() => {
    return canPerformAction('invoices.view') && canPerformAction('invoices.edit');
  }, [canPerformAction]);

  // Check if user can view analytics
  const canViewAnalytics = useCallback(() => {
    return canPerformAction('analytics.view');
  }, [canPerformAction]);

  // Check if user can manage integrations
  const canManageIntegrations = useCallback(() => {
    return canPerformAction('integrations.manage');
  }, [canPerformAction]);

  // Check if user can integrate with Xero
  const canIntegrateXero = useCallback(() => {
    return canPerformAction('integrations.manage') || canPerformAction('invoices.view');
  }, [canPerformAction]);

  // Check if user can approve users
  const canApproveUsers = useCallback(() => {
    return canPerformAction('users.approve');
  }, [canPerformAction]);

  // Check if user can approve members
  const canApproveMembers = useCallback(() => {
    return canPerformAction('members.approve');
  }, [canPerformAction]);

  // Check if user can perform bulk actions
  const canPerformBulkActions = useCallback((resource) => {
    return canPerformAction(`${resource}.bulk_action`);
  }, [canPerformAction]);

  // Check if user can export data
  const canExportData = useCallback((resource) => {
    return canPerformAction(`${resource}.export`);
  }, [canPerformAction]);

  // Get accessible routes based on user role
  const getAccessibleRoutes = useCallback(() => {
    if (!isAuthenticated || !user) return [];

    const userRole = getUserRole();
    
    const routes = {
      'Superuser': [
        '/admin-dashboard',
        '/user-management',
        '/member-management',
        '/invoice-management',
        '/integration-dashboard',
        '/analytics',
        '/settings',
        '/profile'
      ],
      'Staff': [
        '/admin-dashboard',
        '/user-management',
        '/member-management',
        '/invoice-management',
        '/integration-dashboard',
        '/analytics',
        '/profile'
      ],
      'Regular User': [
        '/user-dashboard',
        '/profile'
      ]
    };

    return routes[userRole] || [];
  }, [isAuthenticated, user, getUserRole]);

  // Check if route is accessible
  const isRouteAccessible = useCallback((route) => {
    const accessibleRoutes = getAccessibleRoutes();
    return accessibleRoutes.includes(route);
  }, [getAccessibleRoutes]);

  // Get user permissions summary
  const getUserPermissions = useCallback(() => {
    if (!isAuthenticated || !user) return {};

    const userRole = getUserRole();
    
    return {
      role: userRole,
      isAdmin: isAdmin(),
      isSuperuser: isSuperuser(),
      isStaff: isStaff(),
      isRegularUser: isRegularUser(),
      canManageUsers: canManageUsers(),
      canManageMembers: canManageMembers(),
      canManageInvoices: canManageInvoices(),
      canViewAnalytics: canViewAnalytics(),
      canManageIntegrations: canManageIntegrations(),
      canApproveUsers: canApproveUsers(),
      canApproveMembers: canApproveMembers(),
      accessibleRoutes: getAccessibleRoutes()
    };
  }, [
    isAuthenticated, 
    user, 
    getUserRole, 
    isAdmin, 
    isSuperuser, 
    isStaff, 
    isRegularUser,
    canManageUsers,
    canManageMembers,
    canManageInvoices,
    canViewAnalytics,
    canManageIntegrations,
    canApproveUsers,
    canApproveMembers,
    getAccessibleRoutes
  ]);

  // Check if user can edit specific user
  const canEditUser = useCallback((targetUserId) => {
    if (!isAuthenticated || !user) return false;
    
    // Superusers can edit anyone
    if (isSuperuser()) return true;
    
    // Staff can edit regular users but not other staff/superusers
    if (isStaff()) {
      // This would require fetching the target user's role
      // For now, allow staff to edit any user
      return true;
    }
    
    // Regular users can only edit their own profile
    return user.id === targetUserId;
  }, [isAuthenticated, user, isSuperuser, isStaff]);

  // Check if user can delete specific user
  const canDeleteUser = useCallback((targetUserId) => {
    if (!isAuthenticated || !user) return false;
    
    // Superusers can delete anyone except themselves
    if (isSuperuser()) {
      return user.id !== targetUserId;
    }
    
    // Staff and regular users cannot delete users
    return false;
  }, [isAuthenticated, user, isSuperuser]);

  // Check if user can approve specific user
  const canApproveUser = useCallback((targetUserId) => {
    if (!isAuthenticated || !user) return false;
    
    // Only admins can approve users
    if (!isAdmin()) return false;
    
    // Cannot approve themselves
    return user.id !== targetUserId;
  }, [isAuthenticated, user, isAdmin]);

  // Check if user can manage applications
  const canManageApplications = useCallback(() => {
    return canPerformAction('members.view') && canPerformAction('members.edit');
  }, [canPerformAction]);

  // Check if user can approve applications
  const canApproveApplications = useCallback(() => {
    return canPerformAction('members.approve');
  }, [canPerformAction]);

  // Check if user can delete applications
  const canDeleteApplications = useCallback(() => {
    return canPerformAction('members.delete');
  }, [canPerformAction]);

  return {
    getUserRole,
    hasRole,
    isAdmin,
    isSuperuser,
    isStaff,
    isRegularUser,
    canPerformAction,
    canAccessResource,
    canManageUsers,
    canManageMembers,
    canManageInvoices,
    canViewAnalytics,
    canManageIntegrations,
    canIntegrateXero,
    canApproveUsers,
    canApproveMembers,
    canPerformBulkActions,
    canExportData,
    getAccessibleRoutes,
    isRouteAccessible,
    getUserPermissions,
    canEditUser,
    canDeleteUser,
    canApproveUser,
    canManageApplications,
    canApproveApplications,
    canDeleteApplications
  };
};

export default useRBAC;
